import React, { useState, useRef, useEffect } from 'react';
import { X, Camera } from 'lucide-react';
import { setActiveVisitId, flushGpsQueue } from '../../lib/gpsTracker';
import { safeFetch, extractError } from '../../lib/safeFetch';
import { compressImage } from '../../lib/imageCompression';
import { getPreciseLocation } from '../../lib/preciseLocation';
import GateOtpModal from './GateOtpModal';
import LocationVerifyingModal from './LocationVerifyingModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function haversineKm(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lat2) return 0;
  const R = 6371;
  const toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

export default function EndVisit({ visit, onClose }) {
  const [vehicleKm, setVehicleKm] = useState('');
  const [notes, setNotes] = useState('');
  const [photoBase64, setPhotoBase64] = useState(null);
  const [photoGps, setPhotoGps] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [claimedKm, setClaimedKm] = useState('');
  const [otpModal, setOtpModal] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const fileInputRef = useRef(null);

  const isOwn = (visit?.vehicle_type || 'own') === 'own';
  const isJoined = !!visit?.is_joined;

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => {} // silent — confirm step will block if missing
      );
    }
  }, []);

  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        const gps = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          timestamp: new Date().toISOString()
        };
        setPhotoGps(gps);
        setLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
      });
    }
    const compressed = await compressImage(file);
    setPhotoBase64(compressed);
  };

  const startKm = visit?.start?.vehicle_km || 0;
  const manualKm = vehicleKm ? Math.max(0, parseFloat(vehicleKm) - startKm) : 0;
  const googleKm = location ? haversineKm(visit?.start?.latitude, visit?.start?.longitude, location.latitude, location.longitude) : 0;

  const openConfirm = (e) => {
    e?.preventDefault();
    // Joined visit — straight close, no KM / no photo / no claim.
    if (isJoined) {
      if (window.confirm('End this joined visit?\n\nNo KM or photo required.')) {
        submit(0);
      }
      return;
    }
    if (!photoBase64) { alert('Please capture vehicle KM photo'); return; }
    if (!location) { alert('Waiting for location...'); return; }
    if (!vehicleKm || parseFloat(vehicleKm) <= startKm) { alert('Enter a valid end KM (greater than start KM)'); return; }
    
    if (isOwn) {
      setClaimedKm(manualKm.toFixed(1));
      setConfirming(true);
    } else {
      // Company vehicle — confirm directly without km/claim popup
      if (window.confirm(`End this visit?\n\nDistance: ${manualKm.toFixed(1)} km (company vehicle, no claim)`)) {
        submit(0);
      }
    }
  };

  const submit = async (claimValue) => {
    if (loading) return;
    setLoading(true);

    // Take 5 GPS samples for tight inside/outside-geofence validation at the
    // end-visit moment (overrides the cached `location` set on photo capture).
    setVerifying({ action: 'Visit — END', taken: 0, lastAccuracy: null });
    let preciseLoc;
    try {
      preciseLoc = await getPreciseLocation({
        onProgress: (taken, _t, last) =>
          setVerifying((v) => v && { ...v, taken, lastAccuracy: last?.accuracy ?? v.lastAccuracy }),
      });
    } catch (err) {
      alert(err?.message || 'Location required');
      setVerifying(null); setLoading(false); setConfirming(false); return;
    }
    setVerifying(null);

    const token = localStorage.getItem('token');
    const result = await safeFetch(`${API_URL}/api/visits/${visit.id}/end`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        vehicle_km: isJoined ? 0 : parseFloat(vehicleKm),
        latitude: preciseLoc.latitude,
        longitude: preciseLoc.longitude,
        samples: preciseLoc.samples,
        photo_base64: isJoined ? null : photoBase64,
        photo_gps_lat: isJoined ? null : photoGps?.latitude,
        photo_gps_lon: isJoined ? null : photoGps?.longitude,
        photo_timestamp: isJoined ? null : photoGps?.timestamp,
        notes,
        claimed_km: (isOwn && !isJoined) ? claimValue : 0
      })
    });

    if (result.ok) {
      setActiveVisitId(null);
      flushGpsQueue().catch(() => {});
      setLoading(false);
      setConfirming(false);
      if (result.data?.exit_otp) {
        setOtpModal({
          otp: result.data.exit_otp,
          label: 'Visit — IN',
          subtitle: 'Visit completed',
          autoApproved: false,
        });
      } else {
        onClose();
      }
      return;
    }

    // If the server says the visit isn't ongoing anymore, it's because a
    // previous submit already succeeded (network may have dropped the
    // response). Treat it as success, refresh state, and close the modal.
    const detailLower = String(result.data?.detail || result.text || '').toLowerCase();
    if (result.status === 400 && detailLower.includes('not ongoing')) {
      setActiveVisitId(null);
      flushGpsQueue().catch(() => {});
      onClose();
      setLoading(false);
      setConfirming(false);
      return;
    }

    alert(extractError(result, 'Failed to end visit'));
    setLoading(false);
    setConfirming(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-end sm:items-center justify-center" data-testid="end-visit-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 pb-28 sm:pb-6 max-h-[92vh] overflow-y-auto overscroll-contain">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">End Visit - {visit.supplier_name}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="end-visit-close-btn">
            <X size={20} />
          </button>
        </div>

        {!confirming && !isJoined && (
          <form onSubmit={openConfirm} className="space-y-4">
            <div className={`p-3 rounded-lg text-sm ${isOwn ? 'bg-green-50 text-green-800' : 'bg-blue-50 text-blue-800'}`}>
              Start KM: <strong>{startKm}</strong> • {isOwn ? '🛵 Own vehicle' : '🚐 Company vehicle (no claim)'}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">End Vehicle KM Reading</label>
              <input
                data-testid="end-vehicle-km-input"
                type="number"
                step="0.1"
                value={vehicleKm}
                onChange={(e) => setVehicleKm(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                placeholder="e.g., 12360.5"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Vehicle KM Photo (live, GPS-tagged)</label>
              <input
                data-testid="end-photo-input"
                type="file"
                accept="image/*"
                capture="environment"
                ref={fileInputRef}
                onChange={handlePhotoCapture}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-50"
                data-testid="end-photo-capture-btn"
              >
                <Camera size={20} />
                <span>{photoBase64 ? 'Photo Captured ✓' : 'Capture KM Photo'}</span>
              </button>
              {photoBase64 && <img src={photoBase64} alt="KM" className="mt-2 max-h-32 rounded-lg" />}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Notes (optional)</label>
              <textarea
                data-testid="end-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                rows={2}
                placeholder="What was the outcome?"
              />
            </div>

            <button
              data-testid="end-visit-submit-btn"
              type="submit"
              disabled={loading || !photoBase64}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
            >
              End Visit
            </button>
          </form>
        )}

        {!confirming && isJoined && (
          <div className="space-y-4" data-testid="end-joined-panel">
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4 text-sm text-purple-900">
              <p className="font-semibold mb-1">This is a Joined Visit</p>
              <p className="text-[12px] leading-relaxed">
                You travelled with the host in the same vehicle, so no odometer
                reading, no photo, and no claim is recorded against you. Tap
                <i> End Joined Visit</i> below to close your record.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Notes (optional)</label>
              <textarea
                data-testid="end-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                rows={2}
                placeholder="Anything to record about the visit?"
              />
            </div>

            <button
              onClick={openConfirm}
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
              data-testid="end-joined-submit-btn"
            >
              {loading ? 'Ending…' : 'End Joined Visit'}
            </button>
          </div>
        )}

        {confirming && isOwn && (
          <div className="space-y-4" data-testid="end-confirm-panel">
            <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
              <p className="text-xs text-amber-900 mb-3">Review your trip before submitting for approval.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Google KMs</p>
                  <p className="text-2xl font-bold text-blue-600" data-testid="google-km-display">{googleKm.toFixed(1)}</p>
                  <p className="text-[10px] text-gray-400">straight-line</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Manual (KM diff)</p>
                  <p className="text-2xl font-bold text-gray-700">{manualKm.toFixed(1)}</p>
                  <p className="text-[10px] text-gray-400">{startKm} → {vehicleKm}</p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Claim KMs (editable)</label>
              <input
                data-testid="claim-km-input"
                type="number"
                step="0.1"
                value={claimedKm}
                onChange={(e) => setClaimedKm(e.target.value)}
                className="w-full px-4 py-3 border-2 border-amber-300 rounded-lg text-lg font-semibold"
              />
              <p className="text-xs text-gray-500 mt-1">You can adjust before submitting for admin approval.</p>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => submit(parseFloat(claimedKm || '0'))}
                disabled={loading}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg disabled:opacity-50"
                data-testid="confirm-submit-btn"
              >
                {loading ? 'Submitting...' : 'Submit for Approval'}
              </button>
              <button
                onClick={() => setConfirming(false)}
                className="px-4 py-3 bg-gray-200 rounded-lg font-medium"
              >
                Back
              </button>
            </div>
          </div>
        )}
      </div>

      {verifying && (
        <LocationVerifyingModal action={verifying.action} taken={verifying.taken} lastAccuracy={verifying.lastAccuracy} />
      )}

      {otpModal && (
        <GateOtpModal
          otp={otpModal.otp}
          label={otpModal.label}
          subtitle={otpModal.subtitle}
          autoApproved={otpModal.autoApproved}
          onClose={() => { setOtpModal(null); onClose(); }}
        />
      )}
    </div>
  );
}
